import { ALO_API_HOST } from 'src/scripts/utils/constants';
import { getAloApiAuthHeaders } from 'src/scripts/utils/helpers';
import { getLocationInfo } from 'src/scripts/utils/location';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { ClaimedReward, LLfetchError, LLrewardsResponse, RefundClaimedReward } from './loyaltyTypes';
import { addVariant, changeItemQuantity, processCartData, serverCartData } from '../../../../../utils/cart';
import { loyaltyState, setPointsBalance } from './loyaltyState';

export const fetchLLRewards = async (isCustomer: boolean) => {
  const locationInfo = await getLocationInfo();
  const countryCode = ShippingCountryState.countryCode ? ShippingCountryState.countryCode : locationInfo.country_code;

  const endpointPath = isCustomer ? 'rewards' : 'reward_samples';
  const headers = getAloApiAuthHeaders();
  const response = await fetch(`${ALO_API_HOST}/v1/loyalty/${endpointPath}?country_code=${countryCode}`, { headers });
  const data: LLrewardsResponse | LLfetchError = await response.json();
  return data;
};

export const claimReward = async (rewardId: number, variantId: string, rewardMarket: string) => {
  const headers = getAloApiAuthHeaders();

  const claimRewardResponse = await fetch(`${ALO_API_HOST}/v1/loyalty/claimed_rewards`, {
    method: 'POST',
    body: JSON.stringify({ reward_id: rewardId, variant_id: variantId }),
    headers,
  });

  const response: ClaimedReward = await claimRewardResponse.json();

  if (!response || !response.claimed_reward) {
    console.error('Claim reward error or item is sold out: ', response);
  } else {
    const { claimed_reward } = response;
    const variantId = claimed_reward.redeemable.product.variant_id.toString();
    if (variantId && claimed_reward.state === 'approved')
      setPointsBalance(loyaltyState.pointsBalance - claimed_reward.point_cost);
    const claimId = claimed_reward.id;
    const propertiesToAdd = {
      __lion_context: claimed_reward.redeemable.shopify_cart_line_item_context,
      _loyaltyClaimRewardId: claimId,
      _loyaltyRewardId: claimed_reward.reward.id,
      _market: rewardMarket,
      _expires: Date.parse(claimed_reward.expires_at.toString()),
    };
    const addVariantResponse = await addVariant(
      variantId,
      null,
      { shouldShowATCmodal: false, ATBtype: 'loyalty-redeem-reward' },
      undefined,
      propertiesToAdd
    );
    if (addVariantResponse.data?.items) {
      return { success: true };
    } else {
      // Refund the reward due to inventory issues or failed addVariant()
      await refundClaimedReward(claimId, variantId, undefined);
      // ? [REVISIT] Add UI feedback for the customer
      return { success: false };
    }
  }
};

export const refundClaimedReward = async (reward_claim_id: number, variant_id: string, key: string | undefined) => {
  const headers = getAloApiAuthHeaders();
  // Refund reward in LL system
  const refundRewardResponse = await fetch(`${ALO_API_HOST}/v1/loyalty/claimed_rewards/${reward_claim_id}/refund`, {
    method: 'POST',
    body: JSON.stringify({ reward_id: reward_claim_id, variant_id }),
    headers,
  });

  const response: RefundClaimedReward = await refundRewardResponse.json();
  if (!response || !response.remote_resource_deleted) {
    console.error('Reward refund error or user is signed out: ', response);
  } else {
    setPointsBalance(loyaltyState.pointsBalance + response.claimed_reward.point_cost);
  }

  // Remove from the cart
  if (key) {
    const removeFromCartRes = await changeItemQuantity(key, 0, false);
    return { success: !!(removeFromCartRes && removeFromCartRes.token) };
  }
};

export const removeIneligibleRewardsFromCartAndLLsystem = (
  countryCode: string | null,
  isCustomer: boolean | undefined
) => {
  if (!countryCode) return;
  const cartData = processCartData(serverCartData.cart);
  const { giftItemsAndLoyaltyRewards } = cartData;
  const allRewardsInCart = giftItemsAndLoyaltyRewards.filter(
    (item) => item.properties.__lion_context && item.properties._loyaltyClaimRewardId
  );

  const ineligibleRewards = isCustomer
    ? allRewardsInCart.filter((item) => {
        const currentDate = Date.now();
        const currentMarket = countryCode.toLowerCase() === 'us' ? 'domestic' : 'intl';
        const itemExpiresAt = item.properties._expires || 0; // ? [REVISIT] Remove from cart if no expiration date?
        const itemMarket = item.properties._market;

        return (itemMarket !== currentMarket && itemMarket !== 'global') || currentDate > itemExpiresAt;
      })
    : allRewardsInCart;

  if (ineligibleRewards.length > 0) {
    ineligibleRewards.forEach(async (reward) => {
      await refundClaimedReward(
        Number(reward.properties._loyaltyClaimRewardId),
        reward.variant_id.toString(),
        reward.key
      );
    });
  }
};

export const fetchLLpoints = async (email: string) => {
  const headers = getAloApiAuthHeaders();
  const encodedEmail = encodeURIComponent(email);
  const response = await fetch(`${ALO_API_HOST}/v1/loyalty/points?email=${encodedEmail}`, {
    headers,
  });

  const pointsResponse = await response.json();
  return pointsResponse;
};
